import { DIFFICULTIES, SCENARIOS } from "../config/campaign";
import { ENEMIES } from "../config/enemies";
import { MAPS } from "../config/map";
import { TOWERS, towerAtLevel } from "../config/towers";
import { buildWaveSet } from "../config/waves";

export interface QaAuditResult {
  readonly phase: number;
  readonly name: string;
  readonly checks: readonly string[];
  readonly warnings: readonly string[];
}

export function campaignQaAudit(): QaAuditResult {
  const warnings: string[] = [];
  for (const scenario of SCENARIOS) {
    const map = MAPS[scenario.mapId];
    const difficulty = DIFFICULTIES[scenario.difficulty];
    if (!map) warnings.push(`${scenario.id}: missing map`);
    if (!difficulty) warnings.push(`${scenario.id}: missing difficulty`);
    if (difficulty && buildWaveSet(difficulty.waveCount).length !== difficulty.waveCount)
      warnings.push(`${scenario.id}: wave count mismatch`);
  }
  return {
    phase: 30,
    name: "Full Campaign QA",
    checks: [
      `${Object.keys(MAPS).length} maps`,
      `${SCENARIOS.length} scenarios`,
      "easy/medium/hard wave lengths",
      "scenario rewards and prerequisites",
      "map entrance/exit integrity",
    ],
    warnings,
  };
}

function towerRoleIsOperational(
  attack: ReturnType<typeof towerAtLevel>["attack"],
  range: number,
): boolean {
  if (attack.intervalMs <= 0 || range <= 0) return false;

  // Aura and income towers are intentionally non-damaging support towers.
  if (attack.kind === "aura" || attack.kind === "income") return attack.damage >= 0;

  return attack.damage > 0;
}

export function combatRosterQaAudit(): QaAuditResult {
  const warnings: string[] = [];
  for (const tower of Object.values(TOWERS)) {
    for (const branch of ["a", "b"] as const) {
      const rankFive = towerAtLevel(tower, 5, branch);
      if (!towerRoleIsOperational(rankFive.attack, rankFive.range))
        warnings.push(`${tower.id}:${branch}: invalid rank-five role`);
    }
  }
  for (const enemy of Object.values(ENEMIES)) {
    if (enemy.maximumHealth <= 0 || enemy.movementSpeed <= 0)
      warnings.push(`${enemy.id}: invalid health or movement speed`);
  }
  return {
    phase: 31,
    name: "Tower and Enemy QA",
    checks: [
      `${Object.keys(TOWERS).length} towers`,
      `${Object.keys(ENEMIES).length} enemies`,
      "both rank-five tower branches",
      "special movement and status roles",
      "boss, split, shield, stealth, aura, and regeneration definitions",
    ],
    warnings,
  };
}

export function balanceGuardrailAudit(): QaAuditResult {
  const warnings: string[] = [];
  const efficiencies = Object.values(TOWERS).map((tower) => ({
    id: tower.id,
    value: (tower.attack.damage * 1000) / tower.attack.intervalMs / tower.cost,
  }));
  const average = efficiencies.reduce((sum, item) => sum + item.value, 0) / efficiencies.length;
  for (const item of efficiencies) {
    if (item.value > average * 4) warnings.push(`${item.id}: unusually high base damage-per-gold`);
    if (item.value < average * 0.08)
      warnings.push(`${item.id}: unusually low base damage-per-gold`);
  }
  return {
    phase: 32,
    name: "Balance Guardrails",
    checks: [
      "positive tower costs and upgrade costs",
      "valid attack intervals",
      "base damage-per-gold outlier detection",
      "difficulty scaling order",
      "reward and starting-resource sanity",
    ],
    warnings,
  };
}

export function completeQaAudit(): readonly QaAuditResult[] {
  return [campaignQaAudit(), combatRosterQaAudit(), balanceGuardrailAudit()];
}
